import Keycloak, { KeycloakConfig, KeycloakInitOptions, KeycloakInstance } from 'keycloak-js';
import { AxiosInstance } from 'axios';
import { UserSession, UserProfile, RegisterLoginResult, AcessoSession, Store } from '@/security/keycloak.d';
import HTTP from '@/axios-auth';
import ServiceCandidate from '@/modules/candidate/serviceCandidate';
import CandidateRepositoryAPI from '@/modules/candidate/repository/CandidateRepositoryAPI';

// const isUsuario = (keycloak: KeycloakInstance): boolean =>
//   keycloak?.tokenParsed?.resource_access['detran-digital-api']?.roles?.some((role: string) => role === 'user') || false

class KeycloakHandler {
  private keycloakInstance: KeycloakInstance;
  private isInitialized = false;
  private isInitializing = false;
  private initializationPromise: Promise<RegisterLoginResult> | null = null;

  constructor() {
    const keycloakConfig: KeycloakConfig = {
      url: 'https://sso.prodam.am.gov.br/auth',
      realm: 'am',
      clientId: 'cnhsocial-web',
    };

    this.keycloakInstance = new Keycloak(keycloakConfig);
  }

  async registerLoginListener(openLoginPage = false): Promise<RegisterLoginResult> {
    let authenticated = false;

    // If already initialized, handle login page request or return current state
      // console.log('this.isInitialized', this.isInitialized)
    if (this.isInitialized) {
      if (openLoginPage && !this.keycloakInstance.authenticated) {
        // If login page is requested and user is not authenticated, redirect to login
        try {
          await this.keycloakInstance.login();
        } catch (error) {
          // console.error('Erro ao redirecionar para login do Keycloak', error);
        }
      }
      return { keycloak: this.keycloakInstance, authenticated: this.keycloakInstance.authenticated || false };
    }

    // Prevent multiple simultaneous initializations
    if (this.isInitializing) {
      return this.initializationPromise!;
    }

    this.isInitializing = true;
    // console.log('Starting Keycloak initialization...');

    // Initialize Keycloak for the first time
    const initOptions: KeycloakInitOptions = {
      redirectUri: process.env.VUE_APP_KEYCLOAK_REDIRECT_URI,
      useNonce: false,
      checkLoginIframe: false,
      enableLogging: true,
      flow: 'standard',
      pkceMethod: 'S256',
    };

    // CORREÇÃO: Configurar onLoad apropriadamente para restaurar sessão no refresh
    if (openLoginPage) {
      // Redireciona para login se não autenticado
      initOptions.onLoad = 'login-required';
    } else {
      // Verifica se há sessão SSO existente (cookie do Keycloak)
      // Isso permite restaurar a autenticação após F5/refresh da página
      initOptions.onLoad = 'check-sso';
    }

    this.initializationPromise = (async () => {
      try {
        // console.log('Iniciando Keycloak com as opções:', initOptions);
        authenticated = await this.keycloakInstance.init(initOptions);
        // console.log('this.initializationPromise callback', authenticated)
        
        // CORREÇÃO: Marcar como inicializada independentemente do status de autenticação
        this.isInitialized = true;
        
        // console.log('Keycloak initialization completed, authenticated:', authenticated);
      } catch (error) {
        // console.error('Erro ao inicializar Keycloak', error);
        // Mesmo em caso de erro, marcar como inicializada para evitar tentativas futuras
        this.isInitialized = true;
      } finally {
        this.isInitializing = false;
      }

      // console.log('registerLoginListener','retornando instancia')
      return { keycloak: this.keycloakInstance, authenticated };
    })();

    return this.initializationPromise;
  }

  logout = async (): Promise<void> => {
    try {
      // Armazena a URL de logout do GOV.BR para redirecionamento após o logout do Keycloak
      const govBrLogoutUrl = process.env.AUTH_LOGOUT_GOVBR_LOGOUT_URL;

      // Configura o redirecionamento para a aplicação após o logout do Keycloak
      // A aplicação então redirecionará para o logout do GOV.BR
      const redirectUri = `${process.env.VUE_APP_KEYCLOAK_REDIRECT_URI}?govbr_logout=true&govbr_logout_url=${encodeURIComponent(
        govBrLogoutUrl || ''
      )}`;

      // Limpa o localStorage antes do logout
      window.localStorage.removeItem('acesso-cnhsocial-web');
      window.localStorage.removeItem('usuario-cnhsocial-web');

      // Faz logout no Keycloak
      await this.keycloakInstance.logout({ redirectUri });
    } catch (error) {
      // console.error('Erro ao fazer logout', error);
    }
  };

  setupTokenInterceptor(axiosInstance: AxiosInstance): AxiosInstance {
    axiosInstance.interceptors.request.use(
      (config) => {
        if (config.headers) {
          config.headers.Authorization = `Bearer ${this.keycloakInstance.token}`;
        }
        return Promise.resolve(config);
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    return axiosInstance;
  }

  setupUserSession(store?: Store): void {
    if (!this.keycloakInstance.token) {
      // console.error('Token não disponível');
      return;
    }

    // Decodifica o token manualmente ou usa uma biblioteca de decode
    const userProfile = this.decodeToken(this.keycloakInstance.token) as UserProfile;

    // console.group('Decoded User Profile from Token');
    // console.log(userProfile);
    // console.groupEnd();

    // montando objeto usuario
    // Note: preferred_username from Keycloak/gov.br JWT contains the user's CPF
    const userSession: UserSession = {
      aud: userProfile.aud,
      user_name: userProfile.preferred_username,
      cpf: userProfile.preferred_username, // CPF extracted from JWT token
      scope: ['read', 'write'],
      nome: userProfile.name,
      id: userProfile.sub, // remover
      exp: userProfile.exp, // remover
      jti: userProfile.jti,
      email: userProfile.email,
      client_id: Object.keys(userProfile.resource_access)[0],
    };

    // console.group('userSession');
    // console.log(userSession);
    // console.groupEnd();

    window.localStorage.setItem('usuario-cnhsocial-web', JSON.stringify(userSession));
    store?.commit('setUsuario', userSession);

    // montando objeto acesso
    const acessoSession: AcessoSession = {
      access_token: this.keycloakInstance.token,
      refresh_token: this.keycloakInstance.refreshToken,
      id_token: this.keycloakInstance.idToken,
    };

    window.localStorage.setItem('acesso-cnhsocial-web', JSON.stringify(acessoSession));
    store?.commit('setAcesso', acessoSession);
  }

  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      // console.error('Erro ao decodificar token', error);
      return null;
    }
  }

  checkUserSession() {
    const userSession = window.localStorage.getItem('usuario-cnhsocial-web');

    let session = null;
    let isLoggedIn = false;

    if (userSession) {
      try {
        session = JSON.parse(userSession);
        isLoggedIn = !!(session && session.id);
      } catch (error) {
        // console.error('Erro ao analisar sessão do usuário:', error);
        isLoggedIn = false;
      }
    }

    return { session, isLoggedIn };
  }

  async checkUserExistsInBackend(): Promise<{ exists: boolean; userData?: any }> {
    try {

    // console.group('checkUserExistsInBackend: Iniciando verificação de existência do usuário no backend...');
    // console.log(this.keycloakInstance)
    // console.groupEnd();

      if (!this.keycloakInstance.token) {
        // console.error('checkUserExistsInBackend: Token não disponível');
        throw new Error('Token não disponível');
      }

      // console.log('checkUserExistsInBackend: Token disponível, fazendo chamada para API...');

      // Verifica se o token tem o formato correto (3 segmentos)
      const tokenParts = this.keycloakInstance.token.split('.');
      if (tokenParts.length !== 3) {
        // console.error('checkUserExistsInBackend: Token inválido - número incorreto de segmentos:', tokenParts.length);
        throw new Error(`Token inválido - esperado 3 segmentos, encontrado ${tokenParts.length}`);
      }

      // Cria instância do serviço de candidato
      const candidateRepository = new CandidateRepositoryAPI();
      const candidateService = new ServiceCandidate(candidateRepository);

      // console.log('checkUserExistsInBackend: Iniciando chamada HTTP...');

      // Chama a API do backend - CPF será extraído do JWT token pelo middleware backend
      const userData = await candidateService.findByCpf();

      // console.log('checkUserExistsInBackend: Resposta recebida:', userData);
      
      return { exists: true, userData };
    } catch (error: any) {
      // console.log('checkUserExistsInBackend: Erro capturado:', error);
      // console.log('checkUserExistsInBackend: Tipo do erro:', typeof error);
      // console.log('checkUserExistsInBackend: Error.response:', error.response);
      // console.log('checkUserExistsInBackend: Error.response.status:', error.response?.status);

      // Se o erro for 404, significa que o usuário não existe
      if (error.response && error.response.status === 404) {
        // console.log('checkUserExistsInBackend: Usuário não encontrado (404)');
        return { exists: false };
      }

      // Se for erro de timeout
      if (error.code === 'ECONNABORTED') {
        // console.log('checkUserExistsInBackend: Timeout na requisição');
        return { exists: false };
      }

      // Para outros erros, assumimos que o usuário não existe
      // console.log('checkUserExistsInBackend: Erro não relacionado a 404, assumindo que usuário não existe');
      return { exists: false };
    }
  }

  async handlePostLoginFlow(store?: Store): Promise<{ shouldRedirect: boolean; redirectPath?: string }> {
    try {
      // console.log('Iniciando verificação pós-login...');

      // Configura a sessão do usuário no localStorage
      this.setupUserSession(store);

      // Verifica se o usuário existe no backend
      const userCheck = await this.checkUserExistsInBackend();
      // console.group('handlePostLoginFlow');
      // console.log(userCheck);
      // console.groupEnd();

      if (userCheck.exists) {
        // console.log('Usuário encontrado no backend, redirecionando para perfil');
        return { shouldRedirect: true, redirectPath: '/perfil' };
      } else {
        // console.log('Usuário não encontrado no backend, redirecionando para cadastro');
        return { shouldRedirect: true, redirectPath: '/cadastro' };
      }
    } catch (error) {
      // console.error('Erro no fluxo pós-login:', error);
      // Em caso de erro, redireciona para cadastro por segurança
      return { shouldRedirect: true, redirectPath: '/cadastro' };
    }
  }

  // Adicione este método à sua classe KeycloakHandler
  isUserAuthenticated(): boolean {
    if (!this.isInitialized) {
      return false;
    }
    return this.keycloakInstance.authenticated || false;
  }

  // Método para obter o status atual sem inicializar
  getCurrentStatus(): { initialized: boolean; authenticated: boolean } {
    return {
      initialized: this.isInitialized,
      authenticated: this.isUserAuthenticated()
    };
  }
}

const keycloakHandlerInstance = new KeycloakHandler();

export default keycloakHandlerInstance;